import React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, VariantProps } from 'class-variance-authority'
import { Status } from 'src/constants/enums'
import { cn } from 'src/helpers/style'

export enum TypographyVariant {
  display = 'display',
  headline = 'headline',
  titleXl = 'titleXl',
  titleLg = 'titleLg',
  titleMd = 'titleMd',
  titleSm = 'titleSm',
  titleXs = 'titleXs',
  title2Xs = 'title2Xs',
  headingSm = 'headingSm',
  bodyXl = 'bodyXl',
  bodyLg = 'bodyLg',
  bodyMd = 'bodyMd',
  bodyMdStrong = 'bodyMdStrong',
  bodySm = 'bodySm',
  bodySmStrong = 'bodySmStrong',
  bodyXs = 'bodyXs',
  bodyXsStrong = 'bodyXsStrong',
  bodyXxs = 'bodyXxs',
  bodyXxsStrong = 'bodyXxsStrong',
  labelMd = 'labelMd',
  labelMdStrong = 'labelMdStrong',
  labelSm = 'labelSm',
  caption = 'caption',
  label = 'label',
  link = 'link',
  p = 'p',
  h1 = 'h1',
  h2 = 'h2',
  h3 = 'h3',
  h4 = 'h4',
  h5 = 'h5',
}

export enum TypographyWeight {
  lighter = 'lighter',
  light = 'light',
  regular = 'regular',
  semibold = 'semibold',
  medium = 'medium',
  bold = 'bold',
  extrabold = 'extrabold',
  bolder = 'bolder',
}

export enum TypographySize {
  normal = 'normal',
  xxs = 'xxs',
  xs = 'xs',
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
  '2xl' = '2xl',
  '3xl' = '3xl',
  '4xl' = '4xl',
}

export enum TypographyDecoration {
  underline = 'underline',
  none = 'none',
}

const typographyVariants = cva('', {
  variants: {
    variant: {
      [TypographyVariant.display]: 'font-sans text-display',
      [TypographyVariant.headline]: 'font-sans text-headline',
      [TypographyVariant.titleXl]: 'font-sans text-title-xl',
      [TypographyVariant.titleLg]: 'font-sans text-title-lg',
      [TypographyVariant.titleMd]: 'font-sans text-title-md',
      [TypographyVariant.titleSm]: 'font-sans text-title-sm',
      [TypographyVariant.titleXs]: 'font-sans text-title-xs',
      [TypographyVariant.title2Xs]: 'font-sans text-title-2xs',
      [TypographyVariant.headingSm]: 'font-inter text-heading-sm',
      [TypographyVariant.bodyXl]: 'font-inter text-body-xl',
      [TypographyVariant.bodyLg]: 'font-inter text-body-lg',
      [TypographyVariant.bodyMd]: 'font-inter text-body-md',
      [TypographyVariant.bodyMdStrong]: 'font-inter text-body-md-strong',
      [TypographyVariant.bodySm]: 'font-inter text-body-sm',
      [TypographyVariant.bodySmStrong]: 'font-inter text-body-sm-strong',
      [TypographyVariant.bodyXs]: 'font-inter text-body-xs',
      [TypographyVariant.bodyXsStrong]: 'font-inter text-body-xs-strong',
      [TypographyVariant.bodyXxs]: 'font-inter text-body-xxs',
      [TypographyVariant.bodyXxsStrong]: 'font-inter text-body-xxs-strong',
      [TypographyVariant.labelMd]: 'font-sans text-label-md',
      [TypographyVariant.labelMdStrong]: 'font-sans text-label-md-strong',
      [TypographyVariant.labelSm]: 'font-sans text-label-sm',
      [TypographyVariant.caption]: 'font-inter text-body-xxs uppercase',
      [TypographyVariant.label]:
        'font-sans text-label-md hover:text-brand-accent transition-colors duration-300 ease-in-out',
      [TypographyVariant.link]:
        'font-inter text-body-md px-1 py-0.5 cursor-pointer',
      [TypographyVariant.p]: 'font-inter text-body-md',
      [TypographyVariant.h1]: 'font-sans text-display',
      [TypographyVariant.h2]: 'font-sans text-headline',
      [TypographyVariant.h3]: 'font-sans text-title-xl',
      [TypographyVariant.h4]: 'font-sans text-title-lg',
      [TypographyVariant.h5]: 'font-sans text-title-md',
    },
    weight: {
      [TypographyWeight.lighter]: 'font-lighter',
      [TypographyWeight.light]: 'font-light',
      [TypographyWeight.regular]: 'font-normal',
      [TypographyWeight.semibold]: 'font-semibold',
      [TypographyWeight.medium]: 'font-medium',
      [TypographyWeight.bold]: 'font-bold',
      [TypographyWeight.extrabold]: 'font-extrabold',
      [TypographyWeight.bolder]: 'font-bolder',
    },
    size: {
      [TypographySize.normal]: '',
      [TypographySize.xxs]: 'text-xxs',
      [TypographySize.xs]: 'text-xs',
      [TypographySize.sm]: 'text-sm',
      [TypographySize.md]: 'text-md',
      [TypographySize.lg]: 'text-lg',
      [TypographySize.xl]: 'text-xl',
      [TypographySize['2xl']]: 'text-2xl',
      [TypographySize['3xl']]: 'text-3xl',
      [TypographySize['4xl']]: 'text-4xl',
    },
    status: {
      [Status.default]: 'text-content-primary',
      [Status.error]: '',
      [Status.success]: '',
      [Status.focus]: '',
    },
    decoration: {
      [TypographyDecoration.none]: '',
      [TypographyDecoration.underline]: '',
    },
  },
  compoundVariants: [
    {
      variant: TypographyVariant.label,
      status: Status.error,
      class: 'text-danger',
    },
    {
      variant: TypographyVariant.label,
      status: Status.success,
      class: 'text-success',
    },
    {
      variant: TypographyVariant.label,
      status: Status.focus,
      class: 'text-brand-accent',
    },
    {
      variant: [
        TypographyVariant.display,
        TypographyVariant.headline,
        TypographyVariant.titleXl,
        TypographyVariant.titleLg,
        TypographyVariant.titleMd,
        TypographyVariant.titleSm,
        TypographyVariant.titleXs,
        TypographyVariant.title2Xs,
        TypographyVariant.h1,
        TypographyVariant.h2,
        TypographyVariant.h3,
        TypographyVariant.h4,
        TypographyVariant.h5,
      ],
      class: 'm-0',
    },
    {
      variant: TypographyVariant.link,
      decoration: TypographyDecoration.underline,
      class: 'underline',
    },
  ],
  defaultVariants: {
    variant: TypographyVariant.p,
    size: TypographySize.normal,
    status: Status.default,
    decoration: TypographyDecoration.none,
  },
})

export type ITypographyProps = VariantProps<typeof typographyVariants> & {
  children: React.ReactNode
  /**
   * Classname to adds custom css
   * */
  className?: string
  asChild?: boolean
} & React.HTMLAttributes<HTMLElement> &
  React.AnchorHTMLAttributes<HTMLAnchorElement> &
  React.LabelHTMLAttributes<HTMLLabelElement>

const getTypographyVariantComponent = (
  asChild = false,
  variant?: TypographyVariant,
): React.ElementType => {
  if (asChild) {
    return Slot
  }

  switch (variant) {
    case TypographyVariant.h1:
      return 'h1'
    case TypographyVariant.h2:
      return 'h2'
    case TypographyVariant.h3:
      return 'h3'
    case TypographyVariant.h4:
      return 'h4'
    case TypographyVariant.h5:
      return 'h5'
    case TypographyVariant.label:
      return 'label'
    case TypographyVariant.link:
      return 'a'
    case TypographyVariant.p:
      return 'p'
    case TypographyVariant.caption:
    default:
      return 'span'
  }
}

const Typography = ({
  children,
  variant,
  status,
  size,
  decoration,
  weight,
  className,
  asChild = false,
  ...props
}: ITypographyProps): JSX.Element => {
  const TypographyVariantComponent = getTypographyVariantComponent(
    asChild,
    variant ?? undefined,
  )

  return (
    <TypographyVariantComponent
      className={cn(
        typographyVariants({ variant, status, size, decoration, weight }),
        className,
      )}
      {...props}
    >
      {children}
    </TypographyVariantComponent>
  )
}

export { Typography }
